(() => {
  const productCache = new Map();

  const formatMoney = (amount) => {
    if (window.theme?.Currency?.formatMoney) {
      return window.theme.Currency.formatMoney(amount, window.theme.strings?.moneyFormat);
    }

    return `$${(Number(amount || 0) / 100).toFixed(2)}`;
  };

  const getImageUrl = (image) => {
    if (!image) return "";
    if (typeof image === "string") return image;
    return image.src || image.url || "";
  };

  const getVariantOptions = (variant) => {
    if (Array.isArray(variant?.options)) return variant.options;

    return [variant?.option1, variant?.option2, variant?.option3].filter((option) => option != null);
  };

  const getProduct = (handle) => {
    if (productCache.has(handle)) return productCache.get(handle);

    const root = window.Shopify?.routes?.root || "/";
    const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load product (${response.status})`);
        return response.json();
      })
      .catch((error) => {
        productCache.delete(handle);
        throw error;
      });

    productCache.set(handle, request);
    return request;
  };

  const registerProductModal = () => {
    if (customElements.get("p-sale-product-modal")) return;

    class PSaleProductModal extends HTMLElement {
      constructor() {
        super();

        this.mediaImage = this.querySelector(".p-sale-product-modal__image");
        this.titleElement = this.querySelector(".p-sale-product-modal__title");
        this.typeElement = this.querySelector(".p-sale-product-modal__type");
        this.priceElement = this.querySelector(".p-sale-product-modal__price");
        this.compareAtElement = this.querySelector(".p-sale-product-modal__compare-at");
        this.loadingElement = this.querySelector(".p-sale-product-modal__loading");
        this.optionsElement = this.querySelector(".p-sale-product-modal__options");
        this.quantityElement = this.querySelector(".p-sale-product-modal__quantity");
        this.quantityInput = this.querySelector(".p-sale-product-modal__quantity-input");
        this.quantityButtons = this.querySelectorAll(".p-sale-product-modal__quantity-button");
        this.errorElement = this.querySelector(".p-sale-product-modal__error");
        this.multiAddButton = this.querySelector("multi-add-cart-button");
        this.submitButton = this.querySelector(".p-sale-product-modal__submit");

        this.requestId = 0;
        this.product = null;
        this.currentVariant = null;
        this.productHandle = "";
        this.selectedOptions = [];
        this.optionDefinitions = [];
        this.priceLocked = false;
        this.lockedPrice = null;
        this.fallbackImage = "";
        this.handleModalOpen = this.handleModalOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleMultiAddClick = this.handleMultiAddClick.bind(this);

        this.optionsElement.addEventListener("click", this.handleOptionClick.bind(this));
        this.quantityButtons.forEach((button) => {
          button.addEventListener("click", this.handleQuantityButton.bind(this));
        });
        this.quantityInput.addEventListener("change", this.handleQuantityChange.bind(this));
        this.submitButton.addEventListener("click", this.handleSubmit.bind(this));
      }

      connectedCallback() {
        window.addEventListener("full-screen-modal-open", this.handleModalOpen);
        window.addEventListener("full-screen-modal-close", this.handleModalClose);
        this.multiAddButton.addEventListener("click", this.handleMultiAddClick);
      }

      disconnectedCallback() {
        window.removeEventListener("full-screen-modal-open", this.handleModalOpen);
        window.removeEventListener("full-screen-modal-close", this.handleModalClose);
        this.multiAddButton.removeEventListener("click", this.handleMultiAddClick);
      }

      handleModalOpen(event) {
        if (event.detail?.contentEl !== this) return;

        const opener = document.querySelector(`[aria-controls="${this.id}"]`);
        if (opener) this.loadProduct(opener);
      }

      handleModalClose(event) {
        if (event.detail?.contentEl !== this) return;
        this.requestId += 1;
      }

      handleSubmit(event) {
        if (this.submitButton.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.multiAddButton.click();
      }

      handleMultiAddClick() {
        this.hide();
      }

      hide() {
        this.querySelector(".close")?.click();
      }

      async loadProduct(opener) {
        const requestId = ++this.requestId;
        const card = opener.closest(".p-sale-product-card");
        const selectedOption = card?.querySelector(".p-variant-selector select option:checked");
        const selectedVariantData = this.parseVariantData(selectedOption?.dataset.variant);
        const productHandle = selectedVariantData?.handle || opener.dataset.productHandle;
        this.productHandle = productHandle || "";

        let initialVariantId = Number(selectedVariantData?.id || opener.dataset.productVariantId);
        let initialImage = selectedVariantData?.featured_image_url || opener.dataset.productImage || "";
        let initialPrice = Number(opener.dataset.productPrice || 0);
        let initialFormattedPrice = opener.dataset.productFormattedPrice || formatMoney(initialPrice);
        let initialCompareAtPrice = Number(opener.dataset.productCompareAtPrice || 0);
        let initialFormattedCompareAtPrice = opener.dataset.productFormattedCompareAtPrice || "";

        this.priceLocked = opener.dataset.productPriceLocked === "true";
        this.fallbackImage = initialImage;

        if (!this.priceLocked && selectedVariantData) {
          initialPrice = Number(selectedVariantData.price || 0);
          initialFormattedPrice = selectedVariantData.formattedPrice || formatMoney(initialPrice);
          initialCompareAtPrice = Number(selectedVariantData.compareAtPrice || 0);
          initialFormattedCompareAtPrice =
            selectedVariantData.formattedCompareAtPrice ||
            (initialCompareAtPrice > initialPrice ? formatMoney(initialCompareAtPrice) : "");
        }

        this.lockedPrice = {
          price: initialPrice,
          formattedPrice: initialFormattedPrice,
          compareAtPrice: initialCompareAtPrice,
          formattedCompareAtPrice: initialFormattedCompareAtPrice,
        };

        this.resetModal({
          title: opener.dataset.productTitle || "",
          type: opener.dataset.productType || "",
          image: initialImage,
          submitContent: opener.dataset.productModalSubmitContent || "",
          variantId: initialVariantId,
        });
        this.updatePrice(this.lockedPrice);

        if (!productHandle) {
          this.showError("Product information is unavailable.");
          return;
        }

        try {
          const product = await getProduct(productHandle);
          if (requestId !== this.requestId || this.getAttribute("aria-hidden") !== "false") return;

          this.product = product;
          this.productHandle = product.handle || productHandle;
          this.titleElement.textContent = opener.dataset.productTitle || product.title || "";
          this.updateType(product.type || opener.dataset.productType || "");
          this.optionDefinitions = this.getOptionDefinitions(product);

          let variant = product.variants.find((item) => Number(item.id) === initialVariantId);
          variant = variant || product.variants.find((item) => item.available) || product.variants[0] || null;

          if (!variant) {
            this.showError("No product variants are available.");
            return;
          }

          this.selectedOptions = getVariantOptions(variant);
          this.renderOptions();
          this.loadingElement.hidden = true;
          this.optionsElement.hidden = this.optionsElement.childElementCount === 0;
          this.quantityElement.hidden = false;
          this.applyVariant(variant);
        } catch (error) {
          if (requestId !== this.requestId || this.getAttribute("aria-hidden") !== "false") return;
          console.error("Unable to load p-sale product modal", error);
          this.showError("Unable to load product options. Please try again.");
        }
      }

      parseVariantData(value) {
        if (!value) return null;

        try {
          return JSON.parse(value);
        } catch (error) {
          console.error("Invalid variant data in p-sale product modal", error);
          return null;
        }
      }

      resetModal({ title, type, image, submitContent, variantId }) {
        this.product = null;
        this.currentVariant = null;
        this.selectedOptions = [];
        this.optionDefinitions = [];
        this.optionsElement.replaceChildren();
        this.optionsElement.hidden = true;
        this.quantityElement.hidden = true;
        this.loadingElement.hidden = false;
        this.errorElement.hidden = true;
        this.errorElement.textContent = "";
        this.titleElement.textContent = title;
        this.updateType(type);
        this.updateImage(image, title);
        this.quantityInput.value = "1";
        this.submitButton.innerHTML = submitContent;
        this.setSubmitState(false);
        this.updateCartData(variantId, 1);
        this.updateQuantityButtons();
      }

      getOptionDefinitions(product) {
        return (product.options || []).map((option, index) => {
          const name = typeof option === "string" ? option : option.name;
          const configuredValues = typeof option === "object" && Array.isArray(option.values) ? option.values : [];
          const variantValues = product.variants.map((variant) => getVariantOptions(variant)[index]).filter(Boolean);

          return {
            name: name || `Option ${index + 1}`,
            values: [...new Set(configuredValues.length ? configuredValues : variantValues)],
          };
        });
      }

      renderOptions() {
        this.optionsElement.replaceChildren();

        const isDefaultOnly =
          this.product.variants.length === 1 &&
          this.optionDefinitions.length === 1 &&
          this.optionDefinitions[0].values.length === 1 &&
          this.optionDefinitions[0].values[0] === "Default Title";

        if (isDefaultOnly) return;

        this.optionDefinitions.forEach((option, optionIndex) => {
          const fieldset = document.createElement("fieldset");
          const legend = document.createElement("legend");
          const selectedValue = document.createElement("span");
          const values = document.createElement("div");

          fieldset.className = "p-sale-product-modal__option-group";
          legend.className = "p-sale-product-modal__option-label";
          values.className = "p-sale-product-modal__option-values";
          legend.append(`${option.name}: `, selectedValue);
          selectedValue.dataset.optionSelectedIndex = String(optionIndex);

          option.values.forEach((value) => {
            const button = document.createElement("button");
            const label = document.createElement("span");

            button.type = "button";
            button.className = "p-sale-product-modal__option-button";
            button.dataset.optionIndex = String(optionIndex);
            button.dataset.optionValue = value;
            label.textContent = value;

            if (/colou?r/i.test(option.name)) {
              const optionImage = this.getOptionImage(optionIndex, value);
              if (optionImage) {
                const image = document.createElement("img");
                image.className = "p-sale-product-modal__option-image";
                image.src = optionImage;
                image.alt = "";
                image.width = 40;
                image.height = 40;
                image.loading = "lazy";
                button.appendChild(image);
              }
            }

            button.appendChild(label);
            values.appendChild(button);
          });

          fieldset.append(legend, values);
          this.optionsElement.appendChild(fieldset);
        });
      }

      getOptionImage(optionIndex, value) {
        const variant = this.product.variants.find(
          (item) => getVariantOptions(item)[optionIndex] === value && getImageUrl(item.featured_image),
        );
        return getImageUrl(variant?.featured_image);
      }

      handleOptionClick(event) {
        const button = event.target.closest(".p-sale-product-modal__option-button");
        if (!button || button.disabled) return;

        const optionIndex = Number(button.dataset.optionIndex);
        const optionValue = button.dataset.optionValue;
        const nextSelection = [...this.selectedOptions];
        nextSelection[optionIndex] = optionValue;

        let variant = this.findExactVariant(nextSelection);
        if (!variant?.available) {
          variant = this.product.variants.find((item) => {
            if (!item.available) return false;
            const options = getVariantOptions(item);
            return options.slice(0, optionIndex + 1).every((value, index) => value === nextSelection[index]);
          });
        }

        if (!variant) return;

        this.selectedOptions = getVariantOptions(variant);
        this.applyVariant(variant);
      }

      findExactVariant(selectedOptions) {
        return this.product.variants.find((variant) => {
          const options = getVariantOptions(variant);
          return (
            options.length === selectedOptions.length &&
            options.every((value, index) => value === selectedOptions[index])
          );
        });
      }

      applyVariant(variant) {
        this.currentVariant = variant;
        this.selectedOptions = getVariantOptions(variant);

        const image =
          getImageUrl(variant.featured_image) || getImageUrl(this.product.featured_image) || this.fallbackImage;
        this.updateImage(image, `${this.product.title}${variant.title ? ` - ${variant.title}` : ""}`);

        if (!this.priceLocked) {
          const price = Number(variant.price || 0);
          const compareAtPrice = Number(variant.compare_at_price || 0);
          this.updatePrice({
            price,
            formattedPrice: formatMoney(price),
            compareAtPrice,
            formattedCompareAtPrice: compareAtPrice > price ? formatMoney(compareAtPrice) : "",
          });
        }

        this.updateOptionStates();
        this.updateCartData(variant.id, this.getQuantity());
        this.setSubmitState(variant.available === true);
      }

      updateOptionStates() {
        this.optionsElement.querySelectorAll("[data-option-selected-index]").forEach((element) => {
          element.textContent = this.selectedOptions[Number(element.dataset.optionSelectedIndex)] || "";
        });

        this.optionsElement.querySelectorAll(".p-sale-product-modal__option-button").forEach((button) => {
          const optionIndex = Number(button.dataset.optionIndex);
          const optionValue = button.dataset.optionValue;
          const isSelected = this.selectedOptions[optionIndex] === optionValue;
          const isAvailable = this.product.variants.some((variant) => {
            if (!variant.available) return false;
            const options = getVariantOptions(variant);
            if (options[optionIndex] !== optionValue) return false;

            return options.slice(0, optionIndex).every((value, index) => value === this.selectedOptions[index]);
          });

          button.classList.toggle("is-selected", isSelected);
          button.setAttribute("aria-pressed", String(isSelected));
          button.disabled = !isAvailable;
        });
      }

      handleQuantityButton(event) {
        const action = event.currentTarget.dataset.quantityAction;
        const quantity = this.getQuantity();
        this.quantityInput.value = String(action === "plus" ? quantity + 1 : Math.max(1, quantity - 1));
        this.handleQuantityChange();
      }

      handleQuantityChange() {
        const quantity = this.getQuantity();
        this.quantityInput.value = String(quantity);
        this.updateQuantityButtons();
        this.updateCartData(this.currentVariant?.id || 0, quantity);
      }

      getQuantity() {
        return Math.max(1, Number.parseInt(this.quantityInput.value, 10) || 1);
      }

      updateQuantityButtons() {
        this.quantityButtons.forEach((button) => {
          if (button.dataset.quantityAction === "minus") button.disabled = this.getQuantity() <= 1;
        });
      }

      updateCartData(variantId, quantity) {
        this.multiAddButton.setAttribute(
          "data-products",
          JSON.stringify([
            {
              variantId: Number(variantId || 0),
              quantity: Math.max(1, Number(quantity || 1)),
              handle: this.productHandle,
            },
          ]),
        );
      }

      updatePrice({ price, formattedPrice, compareAtPrice, formattedCompareAtPrice }) {
        this.priceElement.textContent = formattedPrice || formatMoney(price);

        const showCompareAt = Number(compareAtPrice) > Number(price) && Boolean(formattedCompareAtPrice);
        this.compareAtElement.textContent = showCompareAt ? formattedCompareAtPrice : "";
        this.compareAtElement.hidden = !showCompareAt;
      }

      updateType(type) {
        this.typeElement.textContent = type;
        this.typeElement.hidden = !type;
      }

      updateImage(src, alt) {
        this.mediaImage.src = src || "";
        this.mediaImage.alt = src ? alt || "" : "";
        this.mediaImage.hidden = !src;
      }

      setSubmitState(enabled) {
        this.submitButton.disabled = !enabled;
        this.submitButton.setAttribute("aria-disabled", String(!enabled));
      }

      showError(message) {
        this.loadingElement.hidden = true;
        this.optionsElement.hidden = true;
        this.quantityElement.hidden = true;
        this.errorElement.textContent = message;
        this.errorElement.hidden = false;
        this.setSubmitState(false);
      }
    }

    customElements.define("p-sale-product-modal", PSaleProductModal);
  };

  if (customElements.get("full-screen-modal")) {
    registerProductModal();
  } else {
    customElements.whenDefined("full-screen-modal").then(registerProductModal);
  }
})();
